import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Wrap your app with Sentry's Error Boundary
export const SentryErrorBoundary = Sentry.withErrorBoundary(
  ({ children }) => <>{children}</>,
  {
    fallback: ({ error, eventId, resetError }) => {
      const handleTellUs = () => {
        // Sentry's crash-report dialog attaches to the error eventId so the
        // user's description shows up alongside the stack trace in Sentry.
        Sentry.showReportDialog({ eventId });
      };

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-400 via-pink-500 to-purple-600">
          <div className="bg-white/20 backdrop-blur-md border border-white/30 rounded-xl p-8 max-w-md mx-4">
            <h1 className="text-2xl font-bold text-white mb-4">
              Something went wrong
            </h1>
            <p className="text-white/80 mb-6">
              We've been notified and will fix this as soon as possible.
            </p>
            <div className="space-y-3">
              <button
                onClick={resetError}
                className="w-full px-4 py-2 bg-white/30 hover:bg-white/40 text-white rounded-lg transition"
              >
                Try again
              </button>
              <button
                onClick={handleTellUs}
                className="w-full px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition border border-white/30"
              >
                Tell us what happened
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="w-full px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition"
              >
                Go to homepage
              </button>
            </div>

            {/* Show error details in development */}
            {import.meta.env.DEV && (
              <details className="mt-4">
                <summary className="text-white/60 text-sm cursor-pointer">
                  Error details (dev only)
                </summary>
                <pre className="mt-2 text-xs text-white/60 overflow-auto max-h-40">
                  {error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    },
    showDialog: false, // We trigger Sentry's dialog manually via the button.
  }
);

// Track navigation for better error context
export function SentryRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    Sentry.setContext('route', {
      path: location.pathname,
      search: location.search,
    });
  }, [location]);

  return null;
}
